<template>
  <div class="min-h-screen bg-slate-50 flex items-center justify-center p-4">
    <div class="w-full max-w-md">
      <!-- 로고 -->
      <div class="text-center mb-8">
        <h1 class="text-3xl font-bold text-blue-600">물개</h1>
        <p class="text-slate-500 mt-2">부동산 분양 매칭 플랫폼</p>
      </div>

      <!-- 로그인 폼 -->
      <div class="bg-white rounded-2xl shadow-sm p-8">
        <h2 class="text-xl font-semibold text-slate-800 mb-6">
          {{ isRegisterMode ? '회원가입' : '로그인' }}
        </h2>

        <!-- 구글 로그인 -->
        <button
          @click="handleGoogleLogin"
          :disabled="isLoading"
          class="w-full py-3 mb-4 border border-slate-200 rounded-lg flex items-center justify-center gap-3 hover:bg-slate-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg class="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          <span class="text-slate-700 font-medium">Google로 계속하기</span>
        </button>

        <!-- 구분선 -->
        <div class="relative my-6">
          <div class="absolute inset-0 flex items-center">
            <div class="w-full border-t border-slate-200"></div>
          </div>
          <div class="relative flex justify-center text-sm">
            <span class="px-4 bg-white text-slate-400">또는</span>
          </div>
        </div>

        <form @submit.prevent="handleSubmit">
          <!-- 이메일 -->
          <div class="mb-4">
            <label class="block text-sm font-medium text-slate-700 mb-2">이메일</label>
            <input
              v-model="email"
              type="email"
              placeholder="example@email.com"
              class="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 outline-none transition"
              required
            />
          </div>

          <!-- 비밀번호 -->
          <div class="mb-4">
            <label class="block text-sm font-medium text-slate-700 mb-2">비밀번호</label>
            <input
              v-model="password"
              type="password"
              placeholder="6자 이상 입력"
              class="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 outline-none transition"
              required
            />
          </div>

          <!-- 비밀번호 확인 (회원가입 시) -->
          <div v-if="isRegisterMode" class="mb-4">
            <label class="block text-sm font-medium text-slate-700 mb-2">비밀번호 확인</label>
            <input
              v-model="passwordConfirm"
              type="password"
              placeholder="비밀번호 재입력"
              class="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 outline-none transition"
              required
            />
          </div>

          <!-- 에러 메시지 -->
          <div v-if="errorMessage" class="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-lg">
            {{ errorMessage }}
          </div>

          <!-- 제출 버튼 -->
          <button
            type="submit"
            :disabled="isLoading"
            class="w-full py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span v-if="isLoading">처리 중...</span>
            <span v-else>{{ isRegisterMode ? '회원가입' : '로그인' }}</span>
          </button>
        </form>

        <!-- 모드 전환 -->
        <div class="mt-6 text-center text-sm text-slate-500">
          <span v-if="isRegisterMode">
            이미 계정이 있으신가요?
            <button @click="isRegisterMode = false" class="text-blue-600 font-medium hover:underline">
              로그인
            </button>
          </span>
          <span v-else>
            계정이 없으신가요?
            <button @click="isRegisterMode = true" class="text-blue-600 font-medium hover:underline">
              회원가입
            </button>
          </span>
        </div>
      </div>

      <!-- 하단 링크 -->
      <div class="mt-6 text-center text-sm text-slate-400">
        <router-link to="/" class="hover:text-blue-600">메인으로 돌아가기</router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuth } from '@/composables/common/useAuth'

export default {
  name: 'LoginView',
  setup() {
    const router = useRouter()
    const route = useRoute()
    const { login, register, loginWithGoogle } = useAuth()

    const email = ref('')
    const password = ref('')
    const passwordConfirm = ref('')
    const isRegisterMode = ref(false)
    const isLoading = ref(false)
    const errorMessage = ref('')

    const handleRedirect = () => {
      const redirect = route.query.redirect || '/'
      router.push(redirect)
    }

    const handleGoogleLogin = async () => {
      errorMessage.value = ''
      isLoading.value = true

      const result = await loginWithGoogle()
      isLoading.value = false

      if (result.success) {
        handleRedirect()
      } else {
        errorMessage.value = result.error
      }
    }

    const handleSubmit = async () => {
      errorMessage.value = ''

      // 회원가입 시 비밀번호 확인
      if (isRegisterMode.value && password.value !== passwordConfirm.value) {
        errorMessage.value = '비밀번호가 일치하지 않습니다.'
        return
      }

      isLoading.value = true

      let result
      if (isRegisterMode.value) {
        result = await register(email.value, password.value)
      } else {
        result = await login(email.value, password.value)
      }

      isLoading.value = false

      if (result.success) {
        handleRedirect()
      } else {
        errorMessage.value = result.error
      }
    }

    return {
      email,
      password,
      passwordConfirm,
      isRegisterMode,
      isLoading,
      errorMessage,
      handleSubmit,
      handleGoogleLogin
    }
  }
}
</script>
